import { useSelector } from 'react-redux';
import { ProcessEntity } from '@common/enums/processEntity';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IReduxState } from '../../../../../configureStore';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, InputTextArea } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { diagnosticReportAddEditExaminationShowToast } from '../../utils/v1/diagnosticReportAddEditExaminationShowToast';
import { TcorV1DiagnosticReportAddEditCustomControlFields } from './TcorV1DiagnosticReportAddEditCustomControlFields';

interface IInnerTcorV1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
}

interface IInnerTcorV1DiagnosticReportAddEditReduxPropsState {
    selectedEmploymentId: string;
}

export function TcorV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const episodeContext = useEpisodeContext();
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    return (
        <InnerTcorV1DiagnosticReportAddEdit
            {...props}
            selectedEmploymentId={selectedEmploymentId}
            {...episodeContext}
        />
    );
}

class InnerTcorV1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps & IInnerTcorV1DiagnosticReportAddEditReduxPropsState & IEpisodeContext,
    IInnerTcorV1DiagnosticReportAddEditState
> {
    public render() {
        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem={this.props.isFirstItem}
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    title={getServiceLabel(
                        this.props.service.code,
                        'diagnosticReport',
                        translateKey('diagnosticReport.category_examinationReport')
                    )}
                />
                <Form onSubmit={() => null}>
                    <TcorV1DiagnosticReportAddEditCustomControlFields
                        customControlData={this.props.diagnosticReport.customControlData}
                        errors={this.props.errors}
                        handleChangeCustomControl={this.props.handleChangeCustomControl}
                        validateOneCustomControl={this.props.validateOneCustomControl}
                    />

                    <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                        <FormLabel>Opmerkingen</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                automation="remark"
                                error={this.props.errors.get('remark')}
                                onChange={async (value) => await this.props.handleChange('remark', value)}
                                onDoValidate={async () => await this.props.validateOne('remark')}
                                placeholder={translateKey('common.remarksOrQuestions')}
                                value={this.props.diagnosticReport.remark}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertDiagnosticReportChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        await diagnosticReportAddEditExaminationShowToast(
            saveType,
            this.props.service,
            this.props.selectedEmploymentId,
            this.props.patient
        );

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
    }
}
